import { useUserDetailStore } from "@/store/useUserDetailStore"

const PENDING_PREMIUM_SUBSCRIBE_KEY = "pending-premium-subscribe"
const TTL_MS = 24 * 60 * 60 * 1000
export const MAX_PENDING_SUBSCRIBE_SHOWS = 2

export type PendingSubscribeVia = "paypal" | "payssion"

export type PendingPremiumSubscribe = {
  at: number
  hadMembership: boolean
  goodsId: number
  via: PendingSubscribeVia
  shownCount?: number
}

const readStorage = () => {
  if (typeof window === "undefined") return null
  try {
    return localStorage.getItem(PENDING_PREMIUM_SUBSCRIBE_KEY)
      ?? sessionStorage.getItem(PENDING_PREMIUM_SUBSCRIBE_KEY)
  } catch {
    return null
  }
}

const writeStorage = (value: string) => {
  if (typeof window === "undefined") return
  try {
    localStorage.setItem(PENDING_PREMIUM_SUBSCRIBE_KEY, value)
    sessionStorage.removeItem(PENDING_PREMIUM_SUBSCRIBE_KEY)
  } catch { /* private mode */ }
}

const removeStorage = () => {
  if (typeof window === "undefined") return
  try {
    localStorage.removeItem(PENDING_PREMIUM_SUBSCRIBE_KEY)
    sessionStorage.removeItem(PENDING_PREMIUM_SUBSCRIBE_KEY)
  } catch { /* private mode */ }
}

/** 会员判断：vip / vip_type 算会员，ad_free 不算 */
export const hasMembership = (detail?: {
  vip?: boolean
  vip_type?: number | null
} | null) => !!(detail?.vip || detail?.vip_type)

/** 412 次数上限：非会员弹升级；普通会员(vip_type=1)弹升级到皇家月会员 */
export const shouldShowChatLimitUpgrade = (detail?: {
  vip?: boolean
  vip_type?: number | null
} | null) => !hasMembership(detail) || Number(detail?.vip_type) === 1

export const markPendingPremiumSubscribe = (
  goodsId: number,
  via: PendingSubscribeVia = "paypal",
) => {
  if (typeof window === "undefined") return
  const state = useUserDetailStore.getState()
  const payload: PendingPremiumSubscribe = {
    at: Date.now(),
    hadMembership: hasMembership(state),
    goodsId: Number(goodsId),
    via,
    shownCount: 0,
  }
  writeStorage(JSON.stringify(payload))
}

export const clearPendingPremiumSubscribe = () => {
  removeStorage()
}

export const peekPendingPremiumSubscribe = (): PendingPremiumSubscribe | null => {
  const raw = readStorage()
  if (!raw) return null
  try {
    const pending = JSON.parse(raw) as PendingPremiumSubscribe
    if (!pending?.at || Date.now() - pending.at > TTL_MS) {
      removeStorage()
      return null
    }
    return pending
  } catch {
    removeStorage()
    return null
  }
}

export const canShowPendingSubscribeFail = () => {
  const pending = peekPendingPremiumSubscribe()
  return !!pending && (pending.shownCount ?? 0) < MAX_PENDING_SUBSCRIBE_SHOWS
}

export const markPendingSubscribeShown = () => {
  const pending = peekPendingPremiumSubscribe()
  if (!pending) return
  writeStorage(JSON.stringify({
    ...pending,
    shownCount: (pending.shownCount ?? 0) + 1,
  }))
}

export const getPendingSubscribeGoodsId = () => {
  const goodsId = Number(peekPendingPremiumSubscribe()?.goodsId)
  return Number.isFinite(goodsId) && goodsId > 0 ? goodsId : null
}
